// @ts-nocheck
import React, { useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useEffectiveAuth } from '@/hooks/useEffectiveAuth';
import { useUserRoleAssignments } from '@/hooks/useUserRoleAssignments';
import { supabase } from '@/lib/supabase';
import AccessRetryBanner from '@/components/auth/AccessRetryBanner';

const ProtectedRoute: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { user, isLoading: authLoading, profile, isImpersonating, originalUser } = useEffectiveAuth();
  const location = useLocation();

  // A mount log must report mounts, not renders. One line per navigation.
  const renderCount = useRef(0);
  renderCount.current += 1;
  useEffect(() => {
    console.log('✅ ProtectedRoute mounted', location.pathname);
    return () => {
      console.log('↩️ ProtectedRoute unmounted', location.pathname);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    allowedDashboardPaths,
    isAdmin,
    hasMultipleRoles,
    defaultDashboardPath,
    isLoading: roleLoading,
    accessUnknown,
    retry: retryAccess,
    roleAssignments,
  } = useUserRoleAssignments();

  const [parentAccess, setParentAccess] = useState<'pending' | 'granted' | 'denied'>('pending');
  const [scoutAccess, setScoutAccess] = useState<'pending' | 'granted' | 'denied'>('pending');
  const [staffAccess, setStaffAccess] = useState<'pending' | 'granted' | 'denied' | 'error'>('pending');
  const [staffRetryKey, setStaffRetryKey] = useState(0);
  const [sessionResolved, setSessionResolved] = useState(false);

  const isStaffRoute =
    location.pathname === '/staff' ||
    location.pathname.startsWith('/staff/') ||
    location.pathname.startsWith('/staff-dashboard');

  useEffect(() => {
    const checkStaffAccess = async () => {
      if (!isStaffRoute) return;
      // Never decide while the session is still loading.
      if (!user) {
        setStaffAccess(authLoading ? 'pending' : 'denied');
        return;
      }
      setStaffAccess('pending');

      // No shortcut on profile.role — the authoritative check below reads
      // user_roles, organizations and event_staff.


      try {
        const STAFF_ADMIN_ROLES = [
          'admin',
          'org_admin',
          'rlevents_super_admin',
          'rlevents_tenant_admin',
          'rlevents_tenant_staff',
          'staff',
        ];

        const [rolesRes, ownerRes, memberRes, eventStaffRes] = await Promise.all([
          supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', user.id)
            .in('role', STAFF_ADMIN_ROLES),
          supabase
            .from('organizations')
            .select('id')
            .eq('owner_id', user.id)
            .limit(1),
          supabase
            .from('organization_members')
            .select('id')
            .eq('user_id', user.id)
            .eq('status', 'active')
            .limit(1),
          supabase
            .from('event_staff')
            .select('id')
            .eq('user_id', user.id)
            .eq('is_active', true)
            .limit(1),
        ]);

        const anyError = [rolesRes, ownerRes, memberRes, eventStaffRes].some((r) => r.error);
        const isEventStaff = (eventStaffRes.data || []).length > 0;
        if (isEventStaff) {
          setStaffAccess('granted');
          return;
        }
        if (anyError) {
          // A failed read is NOT "no access".
          console.warn('ProtectedRoute: staff access read failed', rolesRes.error || ownerRes.error || memberRes.error || eventStaffRes.error);
          setStaffAccess('error');
          return;
        }
        const hasRole = (rolesRes.data || []).length > 0;
        const isOwner = (ownerRes.data || []).length > 0;
        const isMember = (memberRes.data || []).length > 0;

        setStaffAccess(
          hasRole || isOwner || isMember || isEventStaff ? 'granted' : 'denied'
        );
      } catch (err) {
        console.error('ProtectedRoute: staff access check threw', err);
        setStaffAccess('error');
      }
    };

    if (isStaffRoute) checkStaffAccess();
  }, [isStaffRoute, user?.id, authLoading, staffRetryKey]);

  const searchParams = new URLSearchParams(location.search);
  const profileId = searchParams.get('profile_id');
  const asParent = searchParams.get('asParent');
  const asScout = searchParams.get('asScout');
  const isAthleteRoute = location.pathname.startsWith('/athlete-dashboard');
  const isParentAccess = isAthleteRoute && profileId && asParent === '1';
  const isScoutAccess = isAthleteRoute && profileId && asScout === '1';

  // Magic-link session resolution: on athlete-dashboard, wait for the
  // implicit-flow session to arrive via onAuthStateChange before deciding
  // the user is definitively logged out.
  useEffect(() => {
    if (!isAthleteRoute || user) {
      setSessionResolved(true);
      return;
    }
    if (authLoading) {
      setSessionResolved(false);
      return;
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
        setSessionResolved(true);
      }
    });

    const timeout = setTimeout(() => {
      setSessionResolved(true);
    }, 3000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, [isAthleteRoute, user, authLoading]);

  useEffect(() => {
    const checkParentAccess = async () => {
      if (!isParentAccess || !profile || !user) {
        setParentAccess('denied');
        return;
      }

      try {
        const { data, error } = await supabase
          .from('parent_athlete_relationships')
          .select('*')
          .eq('parent_profile_id', profile.id)
          .eq('athlete_profile_id', profileId)
          .eq('status', 'approved')
          .eq('access_level', 'full')
          .single();

        if (error || !data) {
          console.error('ProtectedRoute: parent_athlete_relationships query failed or empty', error);
          setParentAccess('denied');
        } else {
          setParentAccess('granted');
        }
      } catch (err) {
        console.error('ProtectedRoute: parent access check threw', err);
        setParentAccess('denied');
      }
    };

    const checkScoutAccess = async () => {
      if (!isScoutAccess || !profile || !user) {
        setScoutAccess('denied');
        return;
      }

      try {
        const { data, error } = await supabase
          .from('scout_athlete_relationships')
          .select('*')
          .eq('scout_id', profile.id)
          .eq('athlete_id', profileId)
          .eq('status', 'active')
          .single();

        if (error || !data) {
          console.error('ProtectedRoute: scout_athlete_relationships query failed or empty', error);
          setScoutAccess('denied');
        } else {
          setScoutAccess('granted');
        }
      } catch (err) {
        console.error('ProtectedRoute: scout access check threw', err);
        setScoutAccess('denied');
      }
    };

    if (isParentAccess) checkParentAccess();
    if (isScoutAccess) checkScoutAccess();
  }, [isParentAccess, isScoutAccess, profile, user, profileId]);

  const isLoading = authLoading || roleLoading || 
    (isParentAccess && parentAccess === 'pending') || 
    (isScoutAccess && scoutAccess === 'pending') ||
    (isStaffRoute && staffAccess === 'pending') ||
    (isAthleteRoute && !user && !sessionResolved);

  if (isLoading) {
    console.log("⏰ ProtectedRoute showing loading state");
    return <div className="flex min-h-screen items-center justify-center">
      <div className="text-lg font-medium">Loading...</div>
    </div>;
  }

  if (!user) {
    console.log("❌ ProtectedRoute - No user, redirecting to login");
    return <Navigate to="/auth/login" replace state={{ from: location }} />;
  }

  // A failed or aborted permissions read is NOT a refusal. Keep the session,
  // keep the page, offer a retry.
  if (accessUnknown) {
    return <AccessRetryBanner onRetry={retryAccess} retrying={roleLoading} />;
  }

  if (isParentAccess) {
    if (parentAccess === 'granted') {
      console.log("✅ ProtectedRoute - Parent access granted, rendering children");
      return <div className="protected-route-wrapper">{children ?? <Outlet />}</div>;
    }
    return <Navigate to="/account-not-set-up" replace />;
  }

  if (isScoutAccess) {
    if (scoutAccess === 'granted') {
      console.log("✅ ProtectedRoute - Scout access granted, rendering children");
      return <div className="protected-route-wrapper">{children ?? <Outlet />}</div>;
    }
    return <Navigate to="/scout-dashboard" replace />;
  }

  // Staff Portal access: admin/operator/org member/assigned staff
  if (isStaffRoute) {
    if (staffAccess === 'error') {
      return <AccessRetryBanner onRetry={() => setStaffRetryKey((k) => k + 1)} />;
    }
    if (staffAccess === 'granted') {
      console.log("✅ ProtectedRoute - Staff access granted, rendering children");
      return <div className="protected-route-wrapper">{children ?? <Outlet />}</div>;
    }
    console.log("❌ ProtectedRoute - Staff access denied, redirecting");
    return <Navigate to={defaultDashboardPath || '/auth/login'} replace />;
  }

  // Hotel Partner Portal isolation: hotel_partner-only users are confined to
  // /hotel-portal (plus utility pages). Users who also hold an admin role keep
  // full access and are not redirected. Roles come from user_roles via
  // useUserRoleAssignments — never from profile.role or client storage.
  const isHotelPartnerRoute = location.pathname.startsWith('/hotel-portal');
  const isHotelPartnerOnly = roleAssignments.some((r) => r?.role?.slug === 'hotel_partner');
  if (isHotelPartnerRoute) {
    if (isHotelPartnerOnly || isAdmin) {
      return <div className="protected-route-wrapper">{children ?? <Outlet />}</div>;
    }
    return <Navigate to={defaultDashboardPath || '/auth/login'} replace />;
  }
  if (isHotelPartnerOnly && !isAdmin) {
    const hotelUtilityPaths = ['/billing', '/preferences', '/profile-preferences', '/account', '/auth'];
    const isUtility = hotelUtilityPaths.some((p) => location.pathname.startsWith(p));
    if (!isUtility) {
      console.log("🏨 ProtectedRoute - Hotel partner confined to /hotel-portal, redirecting");
      return <Navigate to="/hotel-portal" replace />;
    }
  }

  const currentPath = location.pathname;
  
  // Allow all authenticated users to access billing
  if (currentPath === '/billing' || currentPath.startsWith('/billing/')) {
    console.log("💳 ProtectedRoute - Billing page access granted for authenticated user");
    return <div className="protected-route-wrapper">{children ?? <Outlet />}</div>;
  }
  
  const hasAccess = allowedDashboardPaths.some(path =>
    currentPath === path || currentPath.startsWith(`${path}/`)
  );

  console.log("🔍 ProtectedRoute - Access check:", { 
    currentPath, 
    hasAccess, 
    isAdmin, 
    allowedPaths: allowedDashboardPaths,
    defaultDashboardPath,
    userRole: profile?.role,
    userId: profile?.id,
    isImpersonating
  });
  
  // Allow admin impersonation to access athlete routes
  if (isImpersonating && originalUser && isAthleteRoute) {
    console.log("🔍 ProtectedRoute - Admin impersonation detected, allowing athlete route access");
    return <div className="protected-route-wrapper">{children ?? <Outlet />}</div>;
  }
  
  // Editor routes get no special case: access is decided by the authoritative
  // role assignments in allowedDashboardPaths, not by profile.role.


  // Block org_admins from accessing /admin routes (except /admin-dashboard which is org admin route)
  const isOrgAdminUser = defaultDashboardPath === '/org-admin-dashboard';
  const isAdminRoute = currentPath.startsWith('/admin') && !currentPath.startsWith('/admin-dashboard');
  
  if (isOrgAdminUser && isAdminRoute) {
    console.log("🚫 ProtectedRoute - Org admin blocked from accessing super admin route:", currentPath);
    return <Navigate to="/org-admin-dashboard" replace />;
  }

  // Special handling for org admin routes
  if (currentPath.startsWith('/org-admin-dashboard')) {
    console.log("🏢 ProtectedRoute - Org admin route detected:", {
      currentPath,
      userRole: profile?.role,
      hasAccess,
      isAdmin
    });
    
    // Org admin access is controlled by user_roles table, already handled in useUserRoleAssignments
    // Super admins can also access
    if (hasAccess || isAdmin) {
      console.log("✅ ProtectedRoute - Org admin or super admin accessing org admin route, allowing access");
      return <div className="protected-route-wrapper">{children ?? <Outlet />}</div>;
    } else {
      console.log("❌ ProtectedRoute - Non-org-admin user trying to access org admin route, redirecting");
      return <Navigate to={defaultDashboardPath || '/auth/login'} replace />;
    }
  }

  // Special handling for press dashboard - allow users with press memberships
  if (currentPath.startsWith('/press-dashboard')) {
    console.log("📰 ProtectedRoute - Press dashboard route detected:", {
      currentPath,
      userRole: profile?.role,
      hasAccess,
      isAdmin
    });
    
    // Check if user has press membership by checking profile_memberships (this would need backend check)
    // For now, allow any authenticated user to access press dashboard if they navigated there
    // The content will be paywalled by PressPaywall component
    console.log("✅ ProtectedRoute - Allowing press dashboard access (content will be paywalled)");
    return <div className="protected-route-wrapper">{children ?? <Outlet />}</div>;
  }

  // Special case: Allow admin users to access staff dashboard
  const isStaffDashboard = currentPath.startsWith('/staff-dashboard');
  const adminCanAccessStaff = isAdmin && isStaffDashboard;
  
  console.log("🏥 Staff dashboard check:", { 
    isStaffDashboard, 
    adminCanAccessStaff, 
    userRole: profile?.role 
  });

  if (hasAccess || isAdmin || adminCanAccessStaff) {
    console.log("✅ ProtectedRoute - Access granted, rendering children");
    return <div className="protected-route-wrapper">{children ?? <Outlet />}</div>;
  }

  if (defaultDashboardPath) {
    console.log("🔄 ProtectedRoute - Redirecting to default dashboard:", defaultDashboardPath);
    return <Navigate to={defaultDashboardPath} replace />;
  }

  console.log("❌ ProtectedRoute - No access, redirecting to login");
  return <Navigate to="/auth/login" replace state={{ from: location }} />;
};

export default ProtectedRoute;
